'use client';
import {
  Badge,
  Box,
  TextField,
  Button,
  Autocomplete,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { FilterList, Search as SearchIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { useCallback, useState, type KeyboardEvent } from 'react';
import { useRouter } from 'next/navigation';
import { MaterialFilterDialog } from './MaterialFilterDialog';
import { useDataPackageFilterStore, defaultFilterCriteria } from '@/stores/dataPackageFilterStore';

import { log } from '@/lib/utils/logger';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { useSnackbar } from '@/hooks/useSnackbar';
import { usePrivileges } from '@/hooks/usePrivileges';
import { PrivilegeEnum } from '@/api/ApiClient';
import { useDialogStore } from '@/stores/dialogStore';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { filterAutocompleteOptionsByMatchRank } from '@/lib/utils/autocompleteMatchRank';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { useReopenSearchPanelOnFailure } from '@/hooks/useReopenSearchPanelOnFailure';
import { markNextSearchUserInitiated } from '@/stores/searchUiStore';

const OPTION_KEYS: OptionKey[] = ['synthesisTechniques', 'characterizationTechniques', 'sampleMaterials'];

type DataPackageSearchPanelProps = {
  /** When true, the title/action header row is omitted (e.g. iframe embed). */
  hideTitleRow?: boolean;
};

export const DataPackageSearchPanel = ({ hideTitleRow = false }: DataPackageSearchPanelProps) => {
  const {
    setFilters,
    resetFilters,
    setPaginationModel,
    filters: appliedCriteria,
    isSearching,
    setIsSearching,
  } = useDataPackageFilterStore();
  const { options, loading, error } = useSelectOptions(OPTION_KEYS);
  const { showSnackbar } = useSnackbar();
  const { isAuthorized } = usePrivileges();
  const { showAddDataPackageDialog } = useDialogStore();
  const router = useRouter();
  const systemConfig = useSystemConfig();

  /** Unsaved edits only. `null` = follow persisted store `appliedCriteria` (no mirror effect). */
  const [filterDraft, setFilterDraft] = useState<typeof defaultFilterCriteria | null>(null);
  const localFilters = filterDraft ?? appliedCriteria;

  const setLocalFilters = useCallback(
    (next: typeof defaultFilterCriteria | ((prev: typeof defaultFilterCriteria) => typeof defaultFilterCriteria)) => {
      setFilterDraft((prevDraft) => {
        const base = prevDraft ?? useDataPackageFilterStore.getState().filters;
        return typeof next === 'function'
          ? (next as (p: typeof defaultFilterCriteria) => typeof defaultFilterCriteria)(base)
          : next;
      });
    },
    []
  );

  const [isExpanded, setIsExpanded] = useState(false);
  useReopenSearchPanelOnFailure(setIsExpanded);
  const [isMaterialDialogOpen, setIsMaterialDialogOpen] = useState(false);

  // Material options for the dialog
  const materialOptions = options.sampleMaterials
    ?.filter((m) => m.id !== undefined && m.name !== undefined && m.name !== 'None')
    .sort((a, b) => a.name!.localeCompare(b.name!))
    .map((m) => ({
      id: m.id!,
      name: m.name!,
    }));

  const handleApply = useCallback(() => {
    markNextSearchUserInitiated();
    setIsSearching(true);
    log.debug('[DataPackageSearchPanel] Applying data package filters:', localFilters);
    setFilters(localFilters);
    setFilterDraft(null);
    setPaginationModel((prevModel) => ({
      ...prevModel,
      page: 0,
    }));
    setIsExpanded(false);
  }, [localFilters, setFilters, setPaginationModel, setIsSearching]);

  const handleReset = useCallback(() => {
    resetFilters();
    setFilterDraft(null);
  }, [resetFilters]);

  const handleSearchOnEnter = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (isSearching) return;
      handleApply();
    },
    [handleApply, isSearching]
  );

  const handleCreateDataPackage = async () => {
    try {
      const dataPackage = await showAddDataPackageDialog();
      if (dataPackage) {
        log.debug('[DataPackageSearchPanel] Data package created:', dataPackage);
        showSnackbar('Data package created successfully', 'success');

        // Navigate to the new data package detail view
        const dataPackageId = dataPackage.doi ?? dataPackage.prePublishId ?? dataPackage.id?.toString();
        if (dataPackageId) {
          router.push(`${systemConfig?.basePath}/data/${encodeURIComponent(dataPackageId)}`);
        }
      }
    } catch (error) {
      log.error('[DataPackageSearchPanel] Failed to create data package:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to create data package.', 'error');
    }
  };

  const canCreateDataPackage = isAuthorized(PrivilegeEnum.DataPackage, 'create');

  if (loading) {
    return (
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body1">Loading...</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body1">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {!hideTitleRow ? (
        <Box display="flex" alignItems="center" gap={2} sx={{ mt: 2, mb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Search Data Packages
          </Typography>
          <Box display="flex" gap={1} sx={{ ml: 'auto' }}>
            {canCreateDataPackage ? (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleCreateDataPackage}
                size="large"
                sx={{
                  minWidth: '160px',
                  fontWeight: 500,
                }}
                startIcon={<AddIcon />}
              >
                New Data Package
              </Button>
            ) : null}
          </Box>
        </Box>
      ) : null}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1.5}
        onKeyDown={handleSearchOnEnter}
        sx={{
          mb: 1.5,
          mt: hideTitleRow ? 1 : 0,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'background.paper',
          py: 1,
          borderRadius: 1,
        }}
      >
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            label="Search in Title, DOI, Authors, and Description"
            variant="outlined"
            size="small"
            value={localFilters.searchText}
            onChange={(e) => setLocalFilters({ ...localFilters, searchText: e.target.value })}
            sx={{ minWidth: 280, flex: 1 }}
          />
        </Box>

        <Box
          display="flex"
          flexWrap="wrap"
          gap={1.5}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ flexShrink: 0 }}
        >
          <Badge
            color="primary"
            variant="dot"
            invisible={
              !(
                (localFilters.dataType && localFilters.dataType !== defaultFilterCriteria.dataType) ||
                (localFilters.synTechniqueId && localFilters.synTechniqueId !== defaultFilterCriteria.synTechniqueId) ||
                (localFilters.charTechniqueId &&
                  localFilters.charTechniqueId !== defaultFilterCriteria.charTechniqueId) ||
                (localFilters.materials && localFilters.materials.length > 0) ||
                (localFilters.elements && localFilters.elements.length > 0)
              )
            }
            sx={{ '& .MuiBadge-badge': { right: 8, top: 8 } }}
          >
            <Button
              variant={isExpanded ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
              startIcon={<FilterList />}
              sx={{ minWidth: 90 }}
            >
              Filter
            </Button>
          </Badge>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{ width: 120 }}
            size="small"
            disabled={isSearching}
            startIcon={isSearching ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isSearching ? 'Searching…' : 'Search'}
          </Button>
          <Button variant="outlined" onClick={handleReset} sx={{ width: 90 }} size="small" color="error">
            Reset
          </Button>
        </Box>
      </Box>

      {isExpanded ? (
        <Box display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Available Data</InputLabel>
            <Select
              value={localFilters.dataType}
              onChange={(e) => setLocalFilters({ ...localFilters, dataType: e.target.value })}
              label="Available Data"
            >
              <MenuItem value="">
                <em>Any</em>
              </MenuItem>
              <MenuItem value="T">Theory Data</MenuItem>
              <MenuItem value="E">Experimental Data</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            options={options.synthesisTechniques ?? []}
            getOptionLabel={(option) => option?.name ?? ''}
            value={options.synthesisTechniques?.find((t) => t.id === localFilters.synTechniqueId) ?? null}
            onChange={(_, newValue) => setLocalFilters({ ...localFilters, synTechniqueId: newValue?.id ?? '' })}
            renderInput={(params) => <TextField {...params} label="Synthesis Techniques" size="small" />}
            sx={{ minWidth: 180 }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          <Autocomplete
            options={options.characterizationTechniques ?? []}
            getOptionLabel={(option) => option?.name ?? ''}
            value={options.characterizationTechniques?.find((t) => t.id === localFilters.charTechniqueId) ?? null}
            onChange={(_, newValue) => setLocalFilters({ ...localFilters, charTechniqueId: newValue?.id ?? '' })}
            renderInput={(params) => <TextField {...params} label="Characterization Techniques" size="small" />}
            sx={{ minWidth: 180 }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          <Autocomplete
            multiple
            disableCloseOnSelect
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            options={materialOptions ?? []}
            getOptionLabel={(option) => option.name}
            filterOptions={(opts, params) => filterAutocompleteOptionsByMatchRank(opts, params, (o) => o.name)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={(() => {
              if (localFilters.elements && localFilters.elements.length > 0) {
                const elementDisplay = localFilters.elements.join(', ');
                return [
                  {
                    id: -1,
                    name: `${elementDisplay} + *`,
                  },
                ];
              }
              return materialOptions?.filter((m) => (localFilters.materials || []).includes(m.id)) ?? [];
            })()}
            onChange={(_, selectedOptions) => {
              setLocalFilters({
                ...localFilters,
                materials: selectedOptions.map((opt) => opt.id),
                elements: [],
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Material"
                size="small"
                sx={{ minWidth: 200 }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      <IconButton size="small" onClick={() => setIsMaterialDialogOpen(true)} sx={{ ml: 1 }}>
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      ) : null}

      {/* Material Filter Dialog */}
      <MaterialFilterDialog
        open={isMaterialDialogOpen}
        onClose={() => setIsMaterialDialogOpen(false)}
        onApply={(selectedIds, selectedElements) => {
          setLocalFilters({
            ...localFilters,
            materials: selectedIds,
            elements: selectedElements,
          });
        }}
        selectedMaterialIds={localFilters.materials ?? []}
        selectedElements={localFilters.elements ?? []}
        allMaterials={materialOptions ?? []}
      />
    </Box>
  );
};
